import { CopyIdButton } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import { formatDateTime, formatLinkStateLabel, formatTargetTypeLabel } from '../format';
import type { BackofficeLink } from '../types';

export interface LinksTableProps {
  links: BackofficeLink[];
  onSelectLink: (link: BackofficeLink) => void;
}

function resolveTargetSummary(link: BackofficeLink): string {
  if (!link.currentDestination) {
    return 'No target';
  }

  if (link.currentDestination.targetType === 'study') {
    return link.currentDestination.project?.title ?? link.currentDestination.projectId ?? 'Study';
  }

  return link.currentDestination.url ?? 'No target';
}

function shouldShowAwaitingStudy(link: BackofficeLink): boolean {
  return (
    link.currentDestination?.targetType === 'study' &&
    link.currentResolution.state === 'awaiting_publish'
  );
}

export function LinksTable({ links, onSelectLink }: LinksTableProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  function shouldHandleRowClick(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return true;
    }

    return !target.closest('button, a, input, select, textarea, [role="button"]');
  }

  if (isMobile) {
    return (
      <div className="divide-y divide-neutral-100">
        {links.map((link) =>
          (() => {
            const awaitingStudy = shouldShowAwaitingStudy(link);
            return (
              <article
                key={link.slug}
                role="button"
                tabIndex={0}
                aria-label={`Open ${link.slug}`}
                className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
                onClick={() => onSelectLink(link)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectLink(link);
                  }
                }}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-body font-semibold text-neutral-900">
                      {link.slug}
                    </div>
                    <div className="truncate text-small text-neutral-500">{link.shortUrl}</div>
                  </div>
                  <span className="inline-flex shrink-0 rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700">
                    {formatLinkStateLabel(link.status)}
                  </span>
                </div>
                <div className="mt-2 text-small text-neutral-600">{resolveTargetSummary(link)}</div>
                <div className="mt-2 text-small text-neutral-500">
                  {formatTargetTypeLabel(link.currentDestination?.targetType ?? 'external_url')}
                </div>
                {awaitingStudy ? (
                  <div className="mt-1 text-small text-neutral-500">Awaiting study</div>
                ) : null}
              </article>
            );
          })()
        )}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse text-left">
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="min-w-[240px] px-4 py-3 font-semibold text-neutral-600">
              Link
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              Target type
            </th>
            <th scope="col" className="min-w-[320px] px-4 py-3 font-semibold text-neutral-600">
              Current target
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              State
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              Updated
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {links.map((link) => {
            const targetSummary = resolveTargetSummary(link);
            const targetType = link.currentDestination?.targetType ?? 'external_url';
            const awaitingStudy = shouldShowAwaitingStudy(link);

            return (
              <tr
                key={link.slug}
                tabIndex={0}
                aria-label={`Open ${link.slug}`}
                className="cursor-pointer hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
                onClick={(event) => {
                  if (shouldHandleRowClick(event.target)) {
                    onSelectLink(link);
                  }
                }}
                onKeyDown={(event) => {
                  if (
                    (event.key === 'Enter' || event.key === ' ') &&
                    shouldHandleRowClick(event.target)
                  ) {
                    event.preventDefault();
                    onSelectLink(link);
                  }
                }}
              >
                <td className="px-4 py-4">
                  <div className="flex max-w-full flex-col items-start gap-1">
                    <div className="group inline-flex max-w-full items-center gap-2">
                      <div className="flex max-w-full flex-col items-start rounded-md text-left">
                        <span className="font-medium text-neutral-900">{link.slug}</span>
                        <span className="text-small text-neutral-500">{link.shortUrl}</span>
                      </div>
                      <CopyIdButton value={link.shortUrl} label="Copy short URL" />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-neutral-700">{formatTargetTypeLabel(targetType)}</td>
                <td className="px-4 py-4 text-neutral-700">{targetSummary}</td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700">
                      {formatLinkStateLabel(link.status)}
                    </span>
                  </div>
                  {awaitingStudy ? (
                    <div className="mt-1 text-small text-neutral-500">Awaiting study</div>
                  ) : null}
                </td>
                <td className="px-4 py-4 text-neutral-700">{formatDateTime(link.updatedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
